/**
 * Load-from-library modal — picks a formula from the user's
 * library + the system seed and hands its tokens (or LaTeX) back
 * to the math editor for in-place loading.
 *
 * Per the user's flow: there is no "save formula to document" path
 * inside this modal. The math editor is mounted inside a document's
 * equation block; whatever you load + edit here is what the document
 * receives when you press Done. Loading from the library replaces
 * the editor's current state — the host equation block decides what
 * to do with the result.
 *
 * Data source: GET /api/Formulas with optional `theme` filter. The
 * 8 theme chips at the top mirror the editor's Common-panel rail.
 * Counts come from GET /api/Formulas/themes; the chip is dimmed
 * (but still clickable) when its theme has 0 entries.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useApiContext } from "@/contexts/ApiContext";
import type { Formula, FormulaThemeCount } from "@/lib/api";
import { THEMES, type ThemeId } from "./commonThemes";
import { renderLatex } from "@/lib/katex-config";
import type { Token } from "./tokens";
import { equationToLatex } from "./tokens";
import { latexToTokens } from "./latexToTokens";
import tutorialFormulas from "./tutorialFormulas.json";

/**
 * The 100 tutorial/example formulas (bundled from lilia-docs, owned by the
 * capture pipeline) surfaced inside the editor's Library as a read-only
 * "Examples" set — this is the formula library that used to live only on the
 * dev-only /math-practice page, now folded into the in-editor Library button.
 *
 * They're a client fixture, not API/DB rows: the set is regenerated upstream
 * with the tutorials, so we map it here rather than seeding Postgres. Tokens
 * are parsed from each formula's LaTeX once at module load, so picking an
 * example drops editable tokens (not just raw LaTeX) into the editor.
 */
interface RawTutorialFormula {
  id: string;
  name: string;
  latex: string;
  section: string;
}
const EXAMPLE_FORMULAS: Formula[] = (tutorialFormulas as RawTutorialFormula[]).map((f) => {
  let tokensJson: string | null = null;
  try {
    tokensJson = JSON.stringify(latexToTokens(f.latex).tokens);
  } catch {
    tokensJson = null; // fall back to LaTeX-only load on a parse miss
  }
  return {
    id: `example:${f.id}`,
    name: f.name,
    latexContent: f.latex,
    category: "example",
    tags: [],
    isFavorite: false,
    isSystem: false,
    usageCount: 0,
    version: 1,
    createdAt: "",
    updatedAt: "",
    theme: f.section, // display-only pill; examples carry no symbol-theme
    slug: f.id,
    tokensJson,
  };
});

function filterExamples(query: string): Formula[] {
  const q = query.trim().toLowerCase();
  if (!q) return EXAMPLE_FORMULAS;
  return EXAMPLE_FORMULAS.filter(
    (f) =>
      f.name.toLowerCase().includes(q) ||
      f.latexContent.toLowerCase().includes(q) ||
      (f.theme ?? "").toLowerCase().includes(q),
  );
}

interface LibraryModalProps {
  open: boolean;
  onClose: () => void;
  /** Active editor theme — pre-selects the matching library filter. */
  defaultTheme: ThemeId;
  /** Called when the user picks a formula. Provides both LaTeX
   *  (always present) and tokens (when stored). The editor decides
   *  how to apply them — tokens are preferred when available. */
  onLoad: (loaded: { formula: Formula; tokens: Token[] | null }) => void;
}

export function LibraryModal({ open, onClose, defaultTheme, onLoad }: LibraryModalProps) {
  const { fetchWithAuth } = useApiContext();
  const [activeTheme, setActiveTheme] = useState<ThemeId | "all" | "examples">(defaultTheme);
  const [query, setQuery] = useState("");
  const [counts, setCounts] = useState<FormulaThemeCount[]>([]);
  const [items, setItems] = useState<Formula[]>([]);
  const [loading, setLoading] = useState(false);

  // Keep modal in sync with the editor's theme if it changes while open.
  useEffect(() => {
    if (open) setActiveTheme(defaultTheme);
  }, [defaultTheme, open]);

  const loadCounts = useCallback(async () => {
    try {
      const res = await fetchWithAuth("/api/formulas/themes");
      if (res.ok) setCounts(await res.json());
    } catch {
      // Counts are decoration — silently keep stale value on failure.
    }
  }, [fetchWithAuth]);

  const loadItems = useCallback(async () => {
    // The "Examples" filter is a pure client fixture — no API round-trip.
    if (activeTheme === "examples") {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({ includeSystem: "true", pageSize: "100" });
      if (activeTheme !== "all") params.set("theme", activeTheme);
      if (query.trim()) params.set("query", query.trim());
      const res = await fetchWithAuth(`/api/formulas?${params}`);
      if (res.ok) {
        const data = await res.json();
        setItems(data.items ?? []);
      }
    } finally {
      setLoading(false);
    }
  }, [activeTheme, query, fetchWithAuth]);

  useEffect(() => {
    if (!open) return;
    loadCounts();
  }, [open, loadCounts]);

  useEffect(() => {
    if (!open) return;
    // Debounce text search; counts + active-theme switches fire fast.
    const t = window.setTimeout(loadItems, 180);
    return () => window.clearTimeout(t);
  }, [open, loadItems]);

  const countFor = useCallback((id: ThemeId) =>
    counts.find((c) => c.theme === id)?.count ?? 0, [counts]);

  // What the grid actually shows: the API results, with the example fixture
  // merged in under "All" or shown alone under "Examples". A specific symbol
  // theme shows only API formulas (examples carry no symbol-theme).
  const displayItems = useMemo<Formula[]>(() => {
    if (activeTheme === "examples") return filterExamples(query);
    if (activeTheme === "all") return [...filterExamples(query), ...items];
    return items;
  }, [activeTheme, query, items]);

  const handlePick = useCallback((f: Formula) => {
    let tokens: Token[] | null = null;
    if (f.tokensJson) {
      try { tokens = JSON.parse(f.tokensJson) as Token[]; }
      catch { tokens = null; }
    }
    onLoad({ formula: f, tokens });
    // Best-effort usage tracker — same hit the legacy formula panel makes.
    // Examples are a client fixture (id `example:*`), not API rows — skip.
    if (!f.id.startsWith("example:")) {
      fetchWithAuth(`/api/formulas/${f.id}/use`, { method: "POST" }).catch(() => {});
    }
    onClose();
  }, [onLoad, onClose, fetchWithAuth]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Load formula from library"
      data-testid="math-library-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(15,10,8,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        className="lilia-math-editor"
        style={{
          background: "var(--lilia-surface)",
          width: "min(960px, 100%)",
          height: "min(680px, 100%)",
          borderRadius: 12,
          boxShadow: "0 20px 60px rgba(0,0,0,0.30)",
          display: "flex", flexDirection: "column", overflow: "hidden",
          color: "var(--lilia-text)",
        }}
      >
        <Header onClose={onClose} count={displayItems.length} />
        <ThemeRail
          activeTheme={activeTheme}
          onChange={setActiveTheme}
          counts={counts}
          countFor={countFor}
        />
        <SearchBar query={query} setQuery={setQuery} />
        <Grid items={displayItems} loading={loading} onPick={handlePick} />
      </div>
    </div>
  );
}

function Header({ onClose, count }: { onClose: () => void; count: number }) {
  return (
    <div style={{
      height: 56, flexShrink: 0,
      borderBottom: "1px solid var(--lilia-border)",
      display: "flex", alignItems: "center", gap: 14,
      padding: "0 18px",
    }}>
      <span style={{
        fontFamily: "var(--lilia-font-serif, Georgia, serif)",
        fontSize: 17, fontWeight: 600,
      }}>Library</span>
      <span style={{
        fontSize: 11, color: "var(--lilia-text-muted)",
        background: "var(--lilia-bg-muted)",
        padding: "3px 8px", borderRadius: 999,
      }}>{count} formulas</span>
      <span style={{ flex: 1 }} />
      <button
        type="button" onClick={onClose}
        style={{
          height: 28, padding: "0 12px", borderRadius: 6, border: 0, cursor: "pointer",
          background: "var(--lilia-bg-muted)",
          color: "var(--lilia-text-secondary)",
          fontSize: 12, fontWeight: 500,
        }}
      >Close · Esc</button>
    </div>
  );
}

function ThemeRail({
  activeTheme, onChange, counts, countFor,
}: {
  activeTheme: ThemeId | "all" | "examples";
  onChange: (t: ThemeId | "all" | "examples") => void;
  counts: FormulaThemeCount[];
  countFor: (id: ThemeId) => number;
}) {
  const totalAll = counts.reduce((acc, c) => acc + c.count, 0) + EXAMPLE_FORMULAS.length;
  return (
    <div style={{
      padding: "8px 12px",
      borderBottom: "1px solid var(--lilia-border)",
      display: "flex", alignItems: "center", gap: 6,
      overflowX: "auto", whiteSpace: "nowrap",
    }}>
      <Chip
        active={activeTheme === "all"}
        onClick={() => onChange("all")}
        glyph="★" label="All" count={totalAll}
      />
      <Chip
        active={activeTheme === "examples"}
        onClick={() => onChange("examples")}
        glyph="✎" label="Examples" count={EXAMPLE_FORMULAS.length}
        testId="math-library-examples-chip"
      />
      {THEMES.map((t) => (
        <Chip
          key={t.id}
          active={activeTheme === t.id}
          onClick={() => onChange(t.id)}
          glyph={t.glyph}
          fontMono={t.font === "mono"}
          italic={t.style === "italic"}
          label={t.chip}
          count={countFor(t.id)}
        />
      ))}
    </div>
  );
}

function Chip({
  active, onClick, glyph, label, count, fontMono, italic, testId,
}: {
  active: boolean; onClick: () => void;
  glyph: string; label: string; count: number;
  fontMono?: boolean; italic?: boolean; testId?: string;
}) {
  return (
    <button
      type="button" onClick={onClick}
      data-testid={testId}
      style={{
        height: 32,
        padding: "0 12px 0 9px",
        display: "inline-flex", alignItems: "center", gap: 7,
        background: active ? "var(--lilia-primary)" : "transparent",
        border: "1px solid transparent",
        borderRadius: 6,
        color: active ? "#fff" : "var(--lilia-text-secondary)",
        cursor: "pointer",
        fontFamily: fontMono
          ? "var(--lilia-font-mono, ui-monospace, monospace)"
          : "var(--lilia-font-serif, Georgia, serif)",
        fontStyle: italic ? "italic" : "normal",
        fontWeight: 500,
        fontSize: 14,
        opacity: count === 0 && !active ? 0.55 : 1,
      }}
    >
      <span>{glyph}</span>
      <span style={{
        fontFamily: "var(--lilia-font-sans, system-ui, sans-serif)",
        fontSize: 12, fontWeight: 600, fontStyle: "normal",
      }}>{label}</span>
      <span style={{
        fontFamily: "var(--lilia-font-mono, monospace)",
        fontSize: 10, opacity: 0.7,
        background: active ? "rgba(255,255,255,0.18)" : "var(--lilia-bg-muted)",
        padding: "1px 5px", borderRadius: 3,
      }}>{count}</span>
    </button>
  );
}

function SearchBar({ query, setQuery }: { query: string; setQuery: (s: string) => void }) {
  return (
    <div style={{
      padding: "10px 12px",
      borderBottom: "1px solid var(--lilia-border)",
    }}>
      <input
        data-testid="math-library-search"
        type="text"
        placeholder="Search by name, description, LaTeX…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
        style={{
          width: "100%", height: 36, padding: "0 12px",
          borderRadius: 8,
          border: "1px solid var(--lilia-border)",
          background: "var(--lilia-surface)",
          fontSize: 13, color: "var(--lilia-text)",
          outline: "none",
        }}
      />
    </div>
  );
}

function Grid({
  items, loading, onPick,
}: {
  items: Formula[]; loading: boolean; onPick: (f: Formula) => void;
}) {
  if (loading && items.length === 0) {
    return <Empty msg="Loading library…" />;
  }
  if (items.length === 0) {
    return <Empty msg="No formulas match this filter. Try a different theme or clear the search." />;
  }
  return (
    <div style={{
      flex: 1, minHeight: 0, overflowY: "auto", padding: 14,
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
      gap: 12,
    }}>
      {items.map((f) => <Card key={f.id} formula={f} onPick={() => onPick(f)} />)}
    </div>
  );
}

function Empty({ msg }: { msg: string }) {
  return (
    <div style={{
      flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
      color: "var(--lilia-text-muted)",
      fontSize: 13, padding: 24, textAlign: "center",
    }}>{msg}</div>
  );
}

/* eslint-disable react-refresh/only-export-components */
function Card({ formula, onPick }: { formula: Formula; onPick: () => void }) {
  const previewHtml = useMemo(() => {
    try { return renderLatex(formula.latexContent, { displayMode: true }); }
    catch { return `<code>${escapeHtml(formula.latexContent)}</code>`; }
  }, [formula.latexContent]);

  return (
    <button
      type="button" onClick={onPick}
      data-testid="math-library-card"
      data-slug={formula.slug ?? ""}
      data-theme={formula.theme ?? ""}
      style={{
        textAlign: "left",
        background: "var(--lilia-surface)",
        border: "1px solid var(--lilia-border)",
        borderRadius: 8,
        padding: 12,
        display: "flex", flexDirection: "column", gap: 8,
        cursor: "pointer",
        transition: "border-color 120ms, box-shadow 120ms",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "var(--lilia-primary)";
        e.currentTarget.style.boxShadow = "0 4px 12px color-mix(in srgb, var(--lilia-primary) 10%, transparent)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--lilia-border)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div style={{
        fontSize: 13, fontWeight: 600,
        color: "var(--lilia-text)",
        lineHeight: 1.3,
      }}>{formula.name}</div>

      <div
        style={{
          minHeight: 56,
          padding: "10px 8px",
          background: "var(--lilia-bg-muted)",
          borderRadius: 6,
          fontSize: 14,
          overflow: "hidden", textAlign: "center",
        }}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: previewHtml }}
      />

      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        fontSize: 10.5,
        color: "var(--lilia-text-muted)",
      }}>
        {formula.theme && (
          <span style={{
            background: "color-mix(in srgb, var(--lilia-primary) 10%, transparent)",
            color: "var(--lilia-primary)",
            padding: "2px 7px", borderRadius: 999,
            fontWeight: 600, letterSpacing: 0.02,
          }}>{formula.theme}</span>
        )}
        {formula.isSystem && (
          <span style={{
            background: "var(--lilia-bg-muted)",
            padding: "2px 7px", borderRadius: 999,
          }}>System</span>
        )}
        <span style={{ flex: 1 }} />
        <span>{formula.tokensJson ? "tokens" : "latex"}</span>
      </div>
    </button>
  );
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Re-exported helper for editor → library save flow.
export function tokensToJson(tokens: Token[]): string {
  return JSON.stringify(tokens);
}

export function snapshotLatex(tokens: Token[]): string {
  return equationToLatex(tokens);
}
